import React from 'react';
import nextCookie from 'next-cookies';
import { get, isEmpty } from 'lodash';

import route from '../constants/routes';
import { USER_TOKEN_COOKIE_KEY, ACTIVE_PROFILE_ID } from '../constants/cookieConstants';
import { setIsLoggedInAction } from '../store/actions';
import {
  getActiveProfileAction,
  getActiveProfileSubscriptionsAction,
  getProfilePermissionsAction,
  getSettings,
  getUserInfoAction,
  getUserPermissionsAction,
  setUserData,
  setUserProfiles,
  setProfileIdsWithAccess,
  getProfileSubscriptionsAction,
  getProfileAccessRequestsAction,
} from '../containers/App/actions';
import { getRegistrationsAction } from '../containers/Authentification/Registration/actions';
import {
  getPaymentMethodsAction,
  getUserSubscriptionsAction,
} from '../containers/UserContainers/AccountSettingsContainer/actions';
import { getHttpInstance } from './httpInstance';
import * as API from './API';
import { AGENT_TYPE_ID, AOS_TYPE_ID } from '../constants/consts';
import { defaultListParams } from './crudUtils';
export const nextPageQueryName = 'page';

export function makeRedirect(ctx, toUrl) {
  const { req, res } = ctx;
  const isServer = !!req;
  if (isServer) {
    const url = req.originalUrl ? `${toUrl}?${nextPageQueryName}=${encodeURIComponent(req.originalUrl)}` : toUrl;
    res.writeHead(302, {
      Location: url,
    });
    res.end();
  } else {
    const url = ctx.asPath ? `${toUrl}?${nextPageQueryName}=${ctx.asPath}` : toUrl;
    window.location.replace(url);
  }
}

export const getToken = ctx => {
  const cookies = nextCookie(ctx);
  return get(cookies, USER_TOKEN_COOKIE_KEY);
};

export const getActiveProfileIdFromCookies = ctx => {
  const cookies = nextCookie(ctx);
  return parseInt(get(cookies, ACTIVE_PROFILE_ID), 10);
};

export const auth = async ctx => {
  let isLoggedIn = false;
  if (ctx && ctx.req && ctx.req.session) {
    const userSession = ctx.req.session.get('ob_user');
    isLoggedIn = !isEmpty(userSession) ? userSession.isLoggedIn : false;
  } else {
    const userSession = await getUserInfo();
    isLoggedIn = !isEmpty(userSession) ? userSession.isLoggedIn : false;
  }
  if (!isLoggedIn) {
    makeRedirect(ctx, route.LOGIN);
  }
};

export const withAuthSync = WrappedComponent => {
  const Wrapper = props => <WrappedComponent {...props} />;

  Wrapper.getInitialProps = async ctx => {
    // check whether user is logged in or not
    await auth(ctx);
    const { store } = ctx;

    const componentProps = WrappedComponent.getInitialProps && (await WrappedComponent.getInitialProps(ctx));

    return { store, ...componentProps };
  };

  return Wrapper;
};

export const getUserInfo = async () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  const url = `${process.env.FRONTBASE_URL}/auth/userSession`;
  const axiosInstance = getHttpInstance(new URL(url).origin);

  let payload;
  try {
    payload = await axiosInstance(url, {
      method: 'GET',
      headers,
    });
  } catch (err) {
    payload = err.response;
  }
  return payload?.data;
};

const setLoginState = async (userInfo, store, serverCookies) => {
  if (userInfo?.isLoggedIn) {
    store.dispatch(setIsLoggedInAction(true));
    // store.dispatch(getUserInfoAction({ ...userInfo?.user }));
    await setUserDetails(userInfo, store, serverCookies);
  } else {
    store.dispatch(setIsLoggedInAction(false));
  }
};

const setUserDetails = async (userInfo, store, serverCookies) => {
  try {
    const response = await API.getUserInfo(userInfo?.user?.id, serverCookies);
    const { data } = response;
    // set userDetails in redux
    const userDetails = get(data, 'userDetails.data', {});
    store.dispatch(setUserData.success(userDetails));

    // set user permissions in redux
    const userPermissions = get(data, 'userPermissions.data.data', []);
    store.dispatch(getUserPermissionsAction.success(userPermissions));

    // set user profiles in redux
    const userProfiles = get(data, 'userProfiles', []);
    store.dispatch(setUserProfiles.success(userProfiles));

    // set user profile ids with access
    const profileIdsWithAccess = get(data, 'profileIdsWithAccess', []);
    store.dispatch(setProfileIdsWithAccess.success(profileIdsWithAccess));

    // set active profile in redux
    const activeProfile = get(data, 'activeProfileData.data', {});
    store.dispatch(getActiveProfileAction.success(activeProfile));

    // set Profile permissions in redux
    const profilePermissions = get(data, 'profilePermissions.data.data', []);
    store.dispatch(getProfilePermissionsAction.success(profilePermissions));

    // set user settings in redux
    const userSettings = get(data, 'userSettings.data', {});
    store.dispatch(getSettings.success(userSettings));

    // set user registrations in redux
    const userRegistrations = get(data, 'userRegistrations.data', []);
    store.dispatch(getRegistrationsAction.success(userRegistrations));

    // set user subscriptions in redux
    const userSubscriptions = get(data, 'userSubscriptions.data', defaultListParams);
    store.dispatch(getUserSubscriptionsAction.success(userSubscriptions));

    // set user cards in redux
    const userPaymentMethods = get(data, 'userPaymentMethods.data', defaultListParams);
    store.dispatch(getPaymentMethodsAction.success(userPaymentMethods));

    store.dispatch(getUserInfoAction.success(data));

    if (activeProfile?.id) {
      const subscriptionsData = await API.getActiveProfileSubscriptions(
        userInfo?.user?.id,
        get(activeProfile, 'id'),
        serverCookies,
      );

      const userSubscriptionOfActiveProfile = get(subscriptionsData, 'data.data');
      store.dispatch(getActiveProfileSubscriptionsAction.success(userSubscriptionOfActiveProfile));
    }

    const profileAccessRequests = get(data, 'profileAccessRequests');
    store.dispatch(getProfileAccessRequestsAction.success(profileAccessRequests));

    const query =
      (activeProfile?.profileType?.id === AGENT_TYPE_ID && { agency_id: activeProfile.agencies[0]?.id }) ||
      (activeProfile?.profileType?.id === AOS_TYPE_ID && { company_id: activeProfile.companies[0]?.company?.id });
    const profileSubscriptionsData = await API.getSubscriptions({ query }, serverCookies);

    const profileSubscriptions = get(profileSubscriptionsData, 'data.data');
    store.dispatch(getProfileSubscriptionsAction.success(profileSubscriptions));
  } catch (err) {
    store.dispatch(
      getUserInfoAction.failure({
        status: err.status,
        message: err.message,
      }),
    );
  } finally {
    store.dispatch(getUserInfoAction.fulfill());
  }
};

export const updateLoginAction = async ctx => {
  const { req, store } = ctx;
  if (typeof window === 'undefined') {
    const serverCookies = {
      cookies: ctx.req.headers.cookie,
      language: ctx.req.language,
    };
    await setLoginState(req.session.get('ob_user'), store, serverCookies);
  }
  // else {
  //   const userInfo = await getUserInfo();
  //   setLoginState(userInfo, store);
  // }
};

export const getCookiesOnServer = ctx => {
  if (!ctx || !ctx.req || !ctx.req.headers) {
    return null;
  }
  return {
    cookies: ctx.req.headers.cookie,
    language: getLanguageOnServer(ctx.req),
  };
};

export const getLanguageOnServer = req => get(req, 'language', 'en');
